import React, { createContext, useContext, useState, useEffect } from 'react';
import { authStorage } from '../utils/secureStorage';

interface User {
  id: string;
  email: string;
  nome?: string;
  tipo_usuario?: string;
  estabelecimento?: {
    id: string;
    nome: string;
    tipo: string;
    slug: string;
  };
  subscription?: {
    plan: string;
    status?: string;
  };
}

interface AuthContextType {
  user: User | null;
  login: (user: User) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Carregar usuário do secureStorage (criptografado) na inicialização
  useEffect(() => {
    const loadUser = async () => {
      try {
        const storedUser = await authStorage.getUserData();
        if (storedUser) {
          setUser(storedUser as unknown as User);
        }
      } catch (error: unknown) {
        console.error('Erro ao carregar usuário do secureStorage:', error);
        authStorage.clearAuth();
      } finally {
        setIsLoading(false);
      }
    };

    loadUser();
  }, []);

  const login = async (userData: User) => {
    setUser(userData);
    await authStorage.setUserData(userData as any, 24); // 24 horas
  };

  const logout = () => {
    setUser(null);
    authStorage.clearAuth(); // Limpa token, refresh token e user data de forma segura
  };

  const value: AuthContextType = {
    user,
    login,
    logout,
    isLoading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};